import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Shield, Mail, ArrowLeft } from 'lucide-react';
import { authAPI } from '@/lib/api';

const AdminForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setSent(true);
      toast.success('Password reset link sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-slate-800 border-slate-700">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-ocean-deep rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-chivo font-bold text-white mb-2">
            Reset Admin Password
          </h1>
          <p className="text-slate-400 text-sm">
            {sent 
              ? 'Check your email for the reset link' 
              : 'Enter your admin email to receive a password reset link'
            }
          </p>
        </div>

        {sent ? (
          <div className="space-y-6">
            <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 text-center">
              <Mail className="w-10 h-10 text-green-400 mx-auto mb-2" />
              <p className="text-green-300 text-sm">
                A password reset link has been sent to <strong>{email}</strong>
              </p>
              <p className="text-slate-400 text-xs mt-2">
                Check your inbox and spam folder
              </p>
            </div>
            
            <Button
              onClick={() => navigate('/mx-admin-access')}
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                placeholder="Enter admin email address"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-ocean-deep hover:bg-ocean"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
            
            <Button
              type="button"
              onClick={() => navigate('/mx-admin-access')}
              variant="ghost"
              className="w-full text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </form>
        )}

        <div className="mt-6 pt-6 border-t border-slate-700">
          <p className="text-xs text-slate-500 text-center">
            Password reset requests are logged for security purposes.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AdminForgotPassword;
